import { useReducer, useEffect, useContext } from 'react'
import {
    UniversalDataTable,
    UniversalModal,
    UniversalToolBar,
    UniversalLoadingCircle,
    UniversalCookieCrumb,
    DataGridLayout,
    Grid,
} from '../../UniversalComponents'
import {
    Archive as ArchiveIcon,
    ArrowOutward as ArrowOutwardIcon,
    AddToQueue as AddToQueueIcon,
    Restore,
    DeleteForever,
    Engineering as EngineeringIcon,
} from '@mui/icons-material'
import {
    getEngines,
    deactivateEngines,
    deleteEngines,
    deleteBlobCalibration,
} from '../../../apiRoutes'
import { useMinimizer } from '../../../context/MinimizeContext'
import { usePermissions, useAuth } from '../../../context/AuthenticationContext'
import {
    permissionsCheck,
    handleActivation,
    handleDeletion,
    standardColumnMapper,
} from '../../../utilities/functionTools'
import EngineInsights from './EngineManagerForm/EngineInsights'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import EngineManagerForm from './EngineManagerForm'

const today = new Date().toISOString().substring(0, 10)

const Engines = () => {
    const setToastMessage = useContext(ToastMessage)

    const currentUser = useAuth()
    const { setUser } = useAuth()
    const { username, activeManufacturer, userPreferences, userProfile, _id } =
        currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile
    const defaultState = {
        open: false,
        row: {},
        data: [],
        viewDeactivated: false,
        formCache: {},
        activateData: [],
        successfullBlobFileDeletion: false,
        etag: '',
        title: '',
        blobUrl: '',
        openEngineInsights: false,
        engines: {
            engine: {
                columns: [
                    {
                        field: 'engineIdentifier',
                        headerName: 'ENGINE IDENTIFIER',
                        width: 400,
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 300,
                    },

                    {
                        field: 'engineName',
                        headerName: 'ENGINE NAME',
                        width: 150,
                    },
                    {
                        field: 'engineCalibrationFileName',
                        headerName: 'Active Calibration File',
                        width: 150,
                    },
                    {
                        field: 'engineDescription',
                        headerName: 'DESCRIPTION',
                        width: 150,
                    },

                    {
                        field: 'updatedBy',
                        headerName: 'UPDATED BY',
                        width: 150,
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.engines?.engine?.density ||
            'standard',
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const { setMinimized } = useMinimizer()

    const {
        open,
        row,
        data,
        activateData,
        viewDeactivated,
        formCache,
        engines,
        density,
        etag,
        title,
        blobUrl,
        successfullBlobFileDeletion,
        openEngineInsights,
    } = state

    const { engine } = engines

    const getData = async (active) => {
        try {
            const data = await getEngines(active)

            if (data && data?.length !== 0) {
                const engines = standardColumnMapper(defaultColumns, data)

                setState({ data: engines })
            } else {
                setState({ data: [] })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const engines =
            userPreferences?.dataTableColumns?.engines?.engine?.columns

        if (Array.isArray(engines)) {
            const mapColumns = engines.map((engine) => ({
                field: engine?.field,
                headerName: engine?.headerName,
                width: engine?.width,
                isVisible: engine?.isVisible,
            }))

            const format = { engine: { columns: mapColumns } }

            setState({ ...state, engines: format })
        }
    }

    const submitColumnData = async () => {
        try {
            setUser({
                key: 'engines',
                value: engines,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivateData = () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'engineName',
            setState,
            getData,
            route: deactivateEngines,
            viewDeactivated,
            setToastMessage,
            page: 'engines',
        })
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'engineName',
            setState,
            getData,
            route: deleteEngines,
            viewDeactivated,
            setToastMessage,
            page: 'engines',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these Engines?`,
            'warning',
            handleDeletionData
        )
    }

    const setDeactivatedView = () => {
        setState({ viewDeactivated: !viewDeactivated })
    }

    useEffect(() => {
        getData(true)
        collectColumnData()
    }, [])

    useEffect(() => {
        getData(!viewDeactivated)
    }, [viewDeactivated, activeManufacturer])

    const defaultColumns = [
        'engineName',
        'engineDescription',
        'updatedBy',
        'manufacturer.manufacturerName',
        'activeEngineCalibrationFile.engineCalibrationFileName',
        'engineIdentifier',
        'tags',
        'engineImageUrl',
    ]

    const visibleColumns = [
        'engineHardwarePartNumber',
        'engineName',
        'engineDescription',
        'updatedBy',
        'manufacturerName',
        'engineCalibrationFileName',
    ]

    const customColumnNames = [
        'Engine Name',
        'Description',
        'Updated By',
        'Manufacturer',
        'Engine Identifier',
        'Active Calibration File',
    ]

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.engineIdentifier) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({
                row: defaultState.row,
                open: !open,
            })
        }
        if (newData !== undefined) {
            return getData(true)
        }
    }

    const buttons = [
        {
            buttonName: 'Add New Engine',
            buttonIcon: <EngineeringIcon />,
            buttonFunction: () => setState({ open: !open }),
            disabled: permissionsCheck(userProfilePermissions, 'Engines'),
        },
        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivateData,
            disabled: permissionsCheck(userProfilePermissions, 'Engines'),
        },
        {
            buttonName: viewDeactivated
                ? 'View Active Engines'
                : 'View Deactivated Engines',
            buttonIcon: <Restore />,
            buttonFunction: setDeactivatedView,
        },
        {
            buttonName: 'Delete',
            buttonIcon: <DeleteForever />,
            buttonFunction: handleDeletionCheck,
            disabled: permissionsCheck(userProfilePermissions, 'Super Admin'),
            display: !viewDeactivated ? 'none' : null,
        },
        // {
        //     buttonName: 'Export',
        //     buttonIcon: <ArrowOutwardIcon />,
        //     buttonFunction: () => {},
        // },
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
        }))

        const setDensity = state.density

        const setData = {
            engine: { ...engine, columns: format, density: setDensity },
        }

        setState({
            engines: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            engines: {
                engine: {
                    ...engine,
                    density: newDensity,
                },
            },
        })
    }

    const handleBlobSetState = (blobInfo) => {
        setState({
            etag: blobInfo?.etag,
            title: blobInfo?.title,
            blobUrl: blobInfo?.url,
        })
    }

    const restBlob = () => {
        setState({ etag: '', blobUrl: '', title: '' })
    }

    const deleteBlob = async (reset?: Boolean) => {
        const creatingData = {
            etag: state.etag,
            containerName: getContainerNameFromUrl(state.blobUrl),
            title: state.title,
        }

        const deletionData = { ...creatingData }

        let res
        try {
            res = await deleteBlobCalibration(deletionData)
            if (res && res.status === 200) {
                const { data } = res

                setToastMessage(`Calibration: ${data?.message}`, 'success')

                setState({
                    open: reset ? true : false,
                    successfullBlobFileDeletion: true,
                })

                restBlob()
                getData(!viewDeactivated)
            } else {
                setToastMessage(
                    'Failed to delete the calibration file',
                    'error'
                )
            }
        } catch (error) {
            setToastMessage(
                'Error occurred while deleting the calibration file',
                'error'
            )
            console.error('Error deleting blob:', error)
        }
    }

    const getContainerNameFromUrl = (url) => {
        const regex =
            /https:\/\/[a-zA-Z0-9-]+\.blob\.core\.windows\.net\/([a-zA-Z0-9-]+)/
        const match = url.match(regex)
        return match ? match[1] : null
    }

    const handleBlobDeletion = async (open, reset?: Boolean) => {
        if (open === true) {
            setToastMessage(
                `Unsaved changes detected, exiting will undo any recent Engine Calibration File changes.`,
                'warning',
                async () => {
                    await deleteBlob(reset)
                }
            )
        }
    }

    const findCalibrationFiles = (data, engineCalibrationFiles) => {
        const time = data?.flatMap((item) => item.engineCalibrationFiles)

        const ids = engineCalibrationFiles?.map((file) => file._id || file.id)

        const calibrationFiles = time.filter((file) =>
            ids?.includes(file._id || file?.id)
        )

        return calibrationFiles
    }
    const resetSuccessfulBlobFileDeletion = () =>
        setState({ successfullBlobFileDeletion: false })

    return (
        <Grid container>
            <DataGridLayout
                cookieCrumbs={<UniversalCookieCrumb />}
                toolbar={<UniversalToolBar buttons={buttons} />}
                dataGrid={
                    <UniversalLoadingCircle
                        data={data}
                        reloadFunction={!viewDeactivated ? getData : () => {}}
                        customTimeoutMessage={
                            !!viewDeactivated &&
                            'No Deactivated Engines Available'
                        }
                    >
                        <UniversalDataTable
                            data={data}
                            apiDensity={density}
                            apiColumns={engines?.engine?.columns}
                            savePreferences={() => submitColumnData()}
                            visibleColumns={[]}
                            customColumnNames={customColumnNames}
                            getRowData={(tableRow) =>
                                // setModalVisbility(tableRow, undefined)
                                setState({
                                    openEngineInsights: true,
                                    row: tableRow,
                                })
                            }
                            onRowSelectionModelChange={(newSelection) => {
                                setState({
                                    activateData: newSelection,
                                })
                            }}
                            onUpdatedColumnsChange={handleUpdatedColumnsChange}
                            onDensityChange={handleDensityChange}
                        />
                    </UniversalLoadingCircle>
                }
            />
            <UniversalModal
                title={`${
                    row.engineIdentifier
                        ? `Engine Identifier: ${row.engineIdentifier}`
                        : 'Configure A New Engine'
                }`}
                timeStamp={`${
                    row.engineIdentifier
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={
                    title.length !== 0 &&
                    blobUrl.length !== 0 &&
                    etag.length !== 0
                        ? () => {
                              handleBlobDeletion(open)
                          }
                        : (tableRow) => {
                              setModalVisbility(tableRow, undefined)
                          }
                }
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <EngineManagerForm
                    row={row}
                    onClose={(tableRow: any, newData: any) => {
                        setModalVisbility(tableRow, newData)
                        restBlob()
                    }}
                    saveCache={(data: any) => setState({ formCache: data })}
                    permissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Engines'
                    )}
                    quickStart={false}
                    blobInfo={handleBlobSetState}
                    calibrationData={findCalibrationFiles(
                        data,
                        row.engineCalibrationFiles
                    )}
                    deleteBlob={handleBlobDeletion}
                    successfullBlobFileDeletion={successfullBlobFileDeletion}
                    resetSuccessfulBlobFileDeletion={
                        resetSuccessfulBlobFileDeletion
                    }
                />
            </UniversalModal>
            {openEngineInsights === true && (
                <UniversalModal
                    title={`Engine Identifier : ${row.engineIdentifier}`}
                    open={openEngineInsights}
                    onClose={() => {
                        setState({
                            openEngineInsights: false,
                            row: defaultState.row,
                        })
                    }}
                >
                    <EngineInsights
                        row={row}
                        openEdit={() =>
                            setState({
                                open: true,
                                openEngineInsights: false,
                            })
                        }
                    />
                </UniversalModal>
            )}
        </Grid>
    )
}

export default Engines
